import { useQuery } from 'react-query';
import { NO_DURATION_DEFINED } from '../../../../helpers/axiosWrapper/cache';
import { isNil, isNotNil } from '../../../../helpers/utils';
import { stopUserInteractionSelector, useStore } from '../hooks/useIMCState';
import { tagsFromTravelerInterest } from '../../_commons/fares';
import { IMCFare } from '../types';
import nanoid from 'nanoid';
import { useState } from 'react';
import { getValidStartDate, getValidEndDate } from './../../../containers/_commons/fares';
const getSortingFilter = (sorting: string) => {
  switch (sorting) {
    case 'price_low_to_high':
      return [
        {
          usdTotalPrice: 'ASC'
        }
      ];
    case 'price_high_to_low':
      return [
        {
          usdTotalPrice: 'DESC'
        }
      ];
    case 'departure_date':
      return [
        {
          departureDate: 'ASC'
        }
      ];
    case 'most_popular':
      return [{ popularity: 'DESC' }];
    case 'no_sorting':
      return [];
    default:
      return [];
  }
};

export const mapFilter = ({
  prePopulation,
  sorting,
  paginationIndex: currentPaginationIndex = undefined,
  faresTransformationLogic,
  budgetSource,
  dateRange = {},
  budget = undefined,
  origins = undefined,
  destinations = undefined,
  flightStopCount = undefined,
  isPrePopulation,
  travelerInterest = {},
  ...restFilter
}: any) => {
  const filterModified = { ...prePopulation, ...restFilter };

  if (faresTransformationLogic === 'histogram') {
    filterModified.origin = origins[0];
    filterModified.destination = destinations[0];
    filterModified.dateRange = dateRange;
  } else {
    let sortingFilter = prePopulation.sorting;
    if (isNotNil(sorting) && Array.isArray(sorting)) {
      sortingFilter = sorting;
    } else if (sorting && typeof sorting === 'string') {
      sortingFilter = getSortingFilter(sorting);
    }
    filterModified.sorting = sortingFilter;

    delete filterModified.origin;
    delete filterModified.destination;
    if (!isPrePopulation) {
      filterModified.origins = origins ? origins : prePopulation.origins;
      filterModified.destinations = destinations ? destinations : prePopulation.destinations;
    }

    delete filterModified.dateRange;
    const start = prePopulation.departure?.start ? prePopulation.departure.start : 0;
    if (prePopulation.flightType === 'ROUND_TRIP') {
      filterModified.departure = dateRange.start
        ? {
            start: getValidStartDate(
              dateRange.start,
              dateRange.flexibleDates,
              dateRange.availableDays,
              start
            ),
            end: getValidEndDate(dateRange.start, dateRange.flexibleDates, dateRange.availableDays)
          }
        : { ...filterModified.departure };
      if (dateRange.end) {
        filterModified.return = {
          start: getValidStartDate(
            dateRange.end,
            dateRange.flexibleDates,
            dateRange.availableDays,
            start
          ),
          end: getValidEndDate(dateRange.end, dateRange.flexibleDates, dateRange.availableDays)
        };
      }
    } else {
      const rangeSelected = dateRange.start && dateRange.end;
      const departSelected = !!dateRange.start;

      if (rangeSelected) {
        filterModified.departure = {
          start: getValidStartDate(
            dateRange.start,
            dateRange.flexibleDates,
            dateRange.availableDays,
            start
          ),
          end: getValidEndDate(dateRange.end, dateRange.flexibleDates, dateRange.availableDays)
        };
      } else if (departSelected) {
        filterModified.departure = {
          start: getValidStartDate(
            dateRange.start,
            dateRange.flexibleDates,
            dateRange.availableDays,
            start
          ),
          end: getValidEndDate(dateRange.start, dateRange.flexibleDates, dateRange.availableDays)
        };
      } else {
        filterModified.departure = { ...filterModified.departure };
      }
    }
  }

  if (isNotNil(currentPaginationIndex)) {
    if (faresTransformationLogic === 'aggregation') {
      filterModified.faresLimit = currentPaginationIndex;
    } else {
      filterModified.routesLimit = currentPaginationIndex;
    }
  }

  delete filterModified.flightStopCount;
  if (flightStopCount !== 'Any' && isNotNil(flightStopCount)) {
    filterModified.flightStopCount = {
      minimum: 0,
      maximum: flightStopCount
    };
  }

  if (budget) {
    filterModified.budget = budgetSource
      ? { maximum: budget, source: budgetSource }
      : { maximum: budget };
  }

  Object.keys(filterModified).forEach((key) => {
    const value = filterModified[key];
    if (isNil(value)) {
      delete filterModified[key];
    } else if (Array.isArray(value) && value[0] === null && value.length === 1) {
      // FIXME: This deletes arrays of the form [null], which are usually a product
      // of context evaluation. We should make context evaluation to be able to
      // handle this case correctly and not send this value.
      delete filterModified[key];
    }
  });

  /**
   * If an origin is defined as part of the request, a market should not be sent.
   * https://everymundo.atlassian.net/browse/FC-2178
   */
  if (filterModified.origin || (filterModified.origins && filterModified.origins[0])) {
    delete filterModified.markets;
  }

  if (travelerInterest) {
    const tags = tagsFromTravelerInterest(travelerInterest);
    if (tags.length > 0) {
      // POr configuracion determinar para donde se envian los tags
      // POr configuración determinar el hierarchy: airport/city/state/country/region
      filterModified['destinationHierarchy'] = {
        tags
      };
    }
    delete filterModified.travelerInterest;
  }

  return filterModified;
};

export interface UseFaresAPIProps {
  getFares: any;
  prePopulation: any;
  paginationSettings?: PaginationSettings;
  integrationSettings: any;
  faresTransformationLogic: string;
  budgetSource?: 'TOTAL_USD_PRICE' | 'TOTAL_PRICE';
  meta: any;
  pageContext: any;
  initialData?: any[];
  cache?: boolean;
  cacheDuration?: number;
  filters: {
    origins?: string[];
    destinations?: string[];
    dateRange: any;
    travelClasses: any;
    flightType: any;
    flightStopCount: any;
    budget: any;
    sorting: any;
    isPrePopulation?: boolean;
    travelerInterest?: any;
    paramsTrackingExtra?: any;
  };
}

const useFaresApi = ({
  getFares,
  prePopulation,
  paginationSettings = { initialVisibleItems: 10 },
  integrationSettings,
  faresTransformationLogic,
  budgetSource,
  meta,
  pageContext,
  initialData,
  cache = false,
  cacheDuration = NO_DURATION_DEFINED,
  filters
}: UseFaresAPIProps) => {
  const {
    origins,
    destinations,
    dateRange,
    travelClasses,
    flightType,
    flightStopCount,
    budget,
    sorting,
    isPrePopulation = false,
    paramsTrackingExtra
  } = filters;

  const { start, end, availableDays, checkedFlexibleDates, flexibleDates } = dateRange;
  const stopUserInteraction = useStore(stopUserInteractionSelector);
  const [id] = useState(() => nanoid());

  return useQuery<IMCFare[]>(
    [
      id,
      origins,
      destinations,
      dateRange,
      travelClasses,
      flightType,
      flightStopCount,
      budget,
      sorting
    ],
    () => {
      const filtersMapped = mapFilter({
        prePopulation,
        sorting,
        paginationIndex: paginationSettings.initialVisibleItems,
        faresTransformationLogic,
        budgetSource,
        origins,
        destinations,
        budget,
        travelClasses,
        flightType,
        flightStopCount,
        dateRange: { start, end, availableDays, checkedFlexibleDates, flexibleDates },
        isPrePopulation,
        paramsTrackingExtra
      });
      return getFares(
        filtersMapped,
        integrationSettings,
        faresTransformationLogic,
        meta,
        pageContext,
        cache,
        cacheDuration
      );
    },
    {
      initialData,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      staleTime: 86400000,
      retry: 3,
      enabled: Boolean(origins && origins.length && destinations && destinations.length),
      onSettled: (data) => {
        stopUserInteraction();
        return data;
      },
      onError: (error) => {
        console.error('Error getFares', error);
      }
    }
  );
};

export default useFaresApi;
